import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../utils/supabase'
import { getCurrencySymbol } from '../utils/currencyUtils'
import { getStatusColor, getStatusLabel } from '../utils/statusUtils'
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Container,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BusinessIcon from '@mui/icons-material/Business'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PhoneIcon from '@mui/icons-material/Phone'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CancelIcon from '@mui/icons-material/Cancel'
import { getServicePrice } from '../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

export default function AppointmentDetailsPage() {
	const { appointmentId } = useParams()
	const navigate = useNavigate()
	const theme = useTheme()
	const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

	const [session, setSession] = useState(null)
	const [loading, setLoading] = useState(true)
	const [appointment, setAppointment] = useState(null)
	const [groupedAppointments, setGroupedAppointments] = useState([])
	const [errorMessage, setErrorMessage] = useState('')
	const [cancelling, setCancelling] = useState(false)

	// Check authentication - allow anonymous access
	useEffect(() => {
		let mounted = true
		const init = async () => {
			const { data } = await supabase.auth.getSession()
			if (!mounted) return

			if (data.session) {
				// User is already authenticated
				setSession(data.session)
				setLoading(false)
			} else {
				// Sign in anonymously to view appointment
				const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously()
				if (anonError) {
					console.error('Anonymous sign-in error:', anonError)
					setErrorMessage('Unable to load appointment. Please try again.')
					setLoading(false)
					return
				}
				if (!mounted) return
				setSession(anonData.session)
				setLoading(false)
			}
		}
		init()
		return () => { mounted = false }
	}, [navigate])

	// Load appointment data
	useEffect(() => {
		if (!session || !appointmentId) return
		loadAppointment()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session, appointmentId])

	const loadAppointment = async () => {
		setErrorMessage('')
		try {
			// For anonymous sessions, just fetch by appointment ID
			// For authenticated sessions, also verify ownership
			const query = supabase
				.from('appointments')
				.select(`
					id,
					start_ts,
					end_ts,
					status,
					created_at,
					customer_name,
					customer_email,
					user_id,
					service_id,
					group_id,
					add_ons,
					service_price,
					service_types!inner(name, duration_minutes, price),
					merchant!inner(name, address, phone, timezone, slug, late_cancel_hours, late_cancel_fee_type, late_cancel_fee_value, no_show_fee_type, no_show_fee_value)
				`)
				.eq('id', appointmentId)

			const { data, error } = await query.single()

			if (error) throw error
			setAppointment(data)

			// Fetch grouped appointments if this is part of a group
			if (data.group_id) {
				const { data: groupData, error: groupError } = await supabase
					.from('appointments')
					.select(`
						id,
						start_ts,
						end_ts,
						service_id,
						pet_id,
						add_ons,
						service_price,
						service_types!inner(name, duration_minutes, price),
						pets(name)
					`)
					.eq('group_id', data.group_id)
					.order('start_ts')

				if (groupError) {
					console.error('Error fetching grouped appointments:', groupError)
				} else {
					setGroupedAppointments(groupData || [])
				}
			} else {
				setGroupedAppointments([])
			}
		} catch (e) {
			setErrorMessage(e.message || 'Failed to load appointment details')
		}
	}


	const canCancelAppointment = () => {
		if (!appointment) return false

		// Can cancel both 'booked' and 'bookPending' appointments
		if (appointment.status !== 'booked' && appointment.status !== 'bookPending') return false

		// Having the appointment ID is sufficient verification (UUID is practically unguessable)
		// This follows the "magic link" security pattern

		const now = dayjs()
		const appointmentStart = dayjs(appointment.start_ts).tz(appointment.merchant.timezone)

		// Can cancel as long as the appointment hasn't started yet
		// Fee will be charged only for 'booked' appointments if within late_cancel_hours window
		// 'bookPending' appointments can be cancelled without any fee
		return now.isBefore(appointmentStart)
	}

	const getCancelDisabledReason = () => {
		if (!appointment) return ''
		if (appointment.status !== 'booked' && appointment.status !== 'bookPending') {
			return 'This appointment cannot be cancelled'
		}

		const now = dayjs()
		const appointmentStart = dayjs(appointment.start_ts).tz(appointment.merchant.timezone)

		if (now.isAfter(appointmentStart)) {
			return 'Cannot cancel a past appointment. The appointment time has already passed.'
		}

		return ''
	}

	const handleCancelAppointment = async () => {
		if (!canCancelAppointment() || cancelling) return

		// For bookPending appointments, simple confirmation without fee warnings
		if (appointment.status === 'bookPending') {
			const confirmed = window.confirm(
				'Are you sure you want to cancel this pending appointment?\n\n' +
				'No cancellation fee will be charged.'
			)
			if (!confirmed) return

			setCancelling(true)
			setErrorMessage('')

			try {
				// Call the cancel-appointment edge function
				const { data, error } = await supabase.functions.invoke('cancel-appointment', {
					body: { appointmentId: appointment.id }
				})

				if (error) throw error

				if (!data.success) {
					throw new Error(data.error || 'Failed to cancel appointment')
				}

				// Update local state
				setAppointment(prev => ({ ...prev, status: 'cancelled' }))

				alert(
					`Appointment cancelled successfully.\n\n` +
					`No cancellation fee was charged.\n\n` +
					`A confirmation email has been sent to ${appointment.customer_email || 'your email'}.`
				)
			} catch (e) {
				setErrorMessage(e.message || 'Failed to cancel appointment')
			} finally {
				setCancelling(false)
			}
			return
		}

		// For booked appointments, check late cancel window and show appropriate warning
		const now = dayjs()
		const appointmentStart = dayjs(appointment.start_ts).tz(appointment.merchant.timezone)
		const lateCancelHours = appointment.merchant.late_cancel_hours || 24
		const hoursUntilAppointment = appointmentStart.diff(now, 'hours', true)
		const withinLateCancelWindow = hoursUntilAppointment < lateCancelHours

		const confirmMessage = withinLateCancelWindow
			? `Are you sure you want to cancel this appointment?\n\n` +
			`⚠️ WARNING: You are cancelling within ${lateCancelHours} hours of the appointment.\n` +
			`A late cancellation fee may be charged based on the store's policy.`
			: `Are you sure you want to cancel this appointment?\n\n` +
			`You are cancelling more than ${lateCancelHours} hours in advance.\n` +
			`No cancellation fee will be charged.`

		const confirmed = window.confirm(confirmMessage)
		if (!confirmed) return

		setCancelling(true)
		setErrorMessage('')

		try {
			// Call the cancel-appointment edge function
			const { data, error } = await supabase.functions.invoke('cancel-appointment', {
				body: { appointmentId: appointment.id }
			})

			if (error) throw error

			if (!data.success) {
				throw new Error(data.error || 'Failed to cancel appointment')
			}

			// Update local state
			setAppointment(prev => ({ ...prev, status: 'cancelled' }))

			// Show fee information if charged
			if (data.fee_charged) {
				const feeDisplay = data.fee_type === 'percentage'
					? `${data.fee_value}% of service price`
					: 'fixed fee'

				alert(
					`Appointment cancelled successfully.\n\n` +
					`Cancellation fee charged: ${getCurrencySymbol(appointment?.merchant?.currency)}${(data.fee_amount / 100).toFixed(2)} (${feeDisplay})\n\n` +
					`A confirmation email has been sent to ${appointment.customer_email || 'your email'}.`
				)
			} else {
				alert(
					`Appointment cancelled successfully.\n\n` +
					`No cancellation fee was charged.\n\n` +
					`A confirmation email has been sent to ${appointment.customer_email || 'your email'}.`
				)
			}
		} catch (e) {
			setErrorMessage(e.message || 'Failed to cancel appointment')
		} finally {
			setCancelling(false)
		}
	}

	if (loading) {
		return (
			<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<CircularProgress />
			</Box>
		)
	}

	if (!session) {
		return null // Redirect handled by useEffect
	}

	if (errorMessage || !appointment) {
		return (
			<Container maxWidth="md" sx={{ py: 6 }}>
				<Card>
					<CardHeader title="Appointment Not Found" />
					<CardContent>
						<Typography color="text.secondary">
							{errorMessage || "We couldn't find this appointment."}
						</Typography>
						<Button
							variant="contained"
							onClick={() => navigate('/dashboard')}
							sx={{ mt: 2 }}
						>
							Back to Dashboard
						</Button>
					</CardContent>
				</Card>
			</Container>
		)
	}

	const appointmentStart = dayjs(appointment.start_ts).tz(appointment.merchant.timezone)
	const appointmentEnd = dayjs(appointment.end_ts).tz(appointment.merchant.timezone)
	const isUpcoming = appointmentStart.isAfter(dayjs())

	// Calculate time range and total price for grouped appointments
	const groupStartTime = groupedAppointments.length > 0
		? dayjs(groupedAppointments[0].start_ts).tz(appointment.merchant.timezone)
		: appointmentStart
	const groupEndTime = groupedAppointments.length > 0
		? dayjs(groupedAppointments[groupedAppointments.length - 1].end_ts).tz(appointment.merchant.timezone)
		: appointmentEnd
	// Calculate total price including add-ons
	const calculateAppointmentTotal = (appt) => {
		const servicePrice = getServicePrice(appt)
		const addOnsPrice = (appt.add_ons || []).reduce((sum, addon) => sum + (addon.price || 0), 0)
		return servicePrice + addOnsPrice
	}

	const totalServicePrice = groupedAppointments.length > 0
		? groupedAppointments.reduce((sum, appt) => sum + calculateAppointmentTotal(appt), 0)
		: calculateAppointmentTotal(appointment)
	const cs = getCurrencySymbol(appointment?.merchant?.currency)

	return (
		<Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
			<Stack spacing={3}>
				{/* Header */}
				<Card>
					<CardHeader
						avatar={<CheckCircleIcon color={getStatusColor(appointment.status)} sx={{ fontSize: 40 }} />}
						title="Appointment Details"
						subheader={`Status: ${getStatusLabel(appointment.status)}`}
						action={
							appointment.user_id && session?.user?.id && appointment.user_id === session.user.id && (
								<Button
									startIcon={<ArrowBackIcon />}
									onClick={() => navigate('/dashboard')}
									size="small"
								>
									Back to Dashboard
								</Button>
							)
						}
					/>
				</Card>

				{/* Appointment Details */}
				<Card>
					<CardHeader title="Appointment Information" />
					<CardContent>
						<Stack spacing={3}>
							<Box sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 1 }}>
								<Stack spacing={2}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
										<BusinessIcon color="primary" />
										<Box>
											<Typography variant="subtitle2">
												{groupedAppointments.length > 1 ? 'Services' : 'Service'}
											</Typography>
											{groupedAppointments.length > 1 ? (
												<Box>
													<Typography variant="h6">{groupedAppointments.length} Services</Typography>
													<Stack spacing={1} sx={{ mt: 1 }}>
														{groupedAppointments.map((appt, index) => (
															<Box key={appt.id} sx={{
																py: 1,
																borderBottom: index < groupedAppointments.length - 1 ? '1px solid rgba(0,0,0,0.1)' : 'none'
															}}>
																<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
																	<Box>
																		<Typography variant="body1">
																			{appt.service_types?.name || 'Service'}
																		</Typography>
																		<Typography variant="body2" color="text.secondary">
																			{appt.pets?.name || 'Pet'} • {dayjs(appt.start_ts).tz(appointment.merchant.timezone).format('h:mm A')} - {dayjs(appt.end_ts).tz(appointment.merchant.timezone).format('h:mm A')}
																		</Typography>
																	</Box>
																	<Typography variant="body2">
																		{cs}{(getServicePrice(appt) / 100).toFixed(2)}
																	</Typography>
																</Box>
																{/* Add-ons for this appointment */}
																{appt.add_ons && appt.add_ons.length > 0 && (
																	<Box sx={{ pl: 2, mt: 0.5 }}>
																		{appt.add_ons.map((addon, addonIndex) => (
																			<Box key={addonIndex} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
																				<Typography variant="body2" color="text.secondary">
																					+ {addon.name}
																				</Typography>
																				<Typography variant="body2" color="text.secondary">
																{cs}{((addon.price || 0) / 100).toFixed(2)}
																</Typography>
																</Box>
															))}
														</Box>
													)}
												</Box>
											))}
											<Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
												<Typography variant="subtitle2">Total:</Typography>
												<Typography variant="subtitle2">
													{cs}{(totalServicePrice / 100).toFixed(2)}
															</Typography>
														</Box>
													</Stack>
												</Box>
											) : (
												<Box>
													<Typography variant="h6">{appointment.service_types.name}</Typography>
													{/* Add-ons for single appointment */}
													{appointment.add_ons && appointment.add_ons.length > 0 && (
														<Box sx={{ mt: 1 }}>
															{appointment.add_ons.map((addon, index) => (
																<Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
																	<Typography variant="body2" color="text.secondary">
																		+ {addon.name}
																	</Typography>
																	<Typography variant="body2" color="text.secondary">
																{cs}{((addon.price || 0) / 100).toFixed(2)}
																</Typography>
																</Box>
															))}
														</Box>
													)}
												</Box>
											)}
										</Box>
									</Box>

									<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
										<CalendarTodayIcon color="primary" />
										<Box>
											<Typography variant="subtitle2">Date & Time</Typography>
											<Typography variant="h6">
												{groupStartTime.format('dddd, MMMM D, YYYY')}
											</Typography>
											<Typography variant="body1">
												{groupStartTime.format('h:mm A')} - {groupEndTime.format('h:mm A')}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												({appointment.merchant.timezone})
											</Typography>
										</Box>
									</Box>

									<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
										<AccessTimeIcon color="primary" />
										<Box>
											<Typography variant="subtitle2">Duration</Typography>
											<Typography variant="body1">
												{groupedAppointments.length > 0
													? `${groupEndTime.diff(groupStartTime, 'minute')} minutes`
													: `${appointment.service_types.duration_minutes} minutes`
												}
											</Typography>
										</Box>
									</Box>
								</Stack>
							</Box>

							{/* Store Information */}
							<Box sx={{ p: 3, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
								<Typography variant="h6" gutterBottom>
									{appointment.merchant.name}
								</Typography>
								<Stack spacing={1}>
									{appointment.merchant.address && (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<LocationOnIcon fontSize="small" />
											<Typography variant="body2">
												{appointment.merchant.address}
											</Typography>
										</Box>
									)}
									{appointment.merchant.phone && (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<PhoneIcon fontSize="small" />
											<Typography variant="body2">
												{appointment.merchant.phone}
											</Typography>
										</Box>
									)}
								</Stack>
							</Box>

							{/* Booking Information */}
							<Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
								<Typography variant="subtitle2" gutterBottom>
									Booking Details
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Appointment ID: {appointment.id}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Booked on: {dayjs(appointment.created_at).format('MMMM D, YYYY [at] h:mm A')}
								</Typography>
							</Box>

							{/* Cancellation & No-Show Policy */}
							<Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, border: 1, borderColor: 'warning.main' }}>
								<Typography variant="subtitle2" gutterBottom fontWeight="bold">
									Cancellation & No-Show Policy
								</Typography>

								{/* Late Cancellation Policy */}
								{appointment.merchant.late_cancel_hours &&
									appointment.merchant.late_cancel_fee_type &&
									appointment.merchant.late_cancel_fee_value != null ? (
									<Box sx={{ mb: 1 }}>
										<Typography variant="body2" color="text.primary">
											<strong>Late Cancellation:</strong> Cancellations within {appointment.merchant.late_cancel_hours} hours of the appointment will incur a fee of{' '}
										{appointment.merchant.late_cancel_fee_type === 'percentage'
											? `${appointment.merchant.late_cancel_fee_value}% of the service price (${cs}${(totalServicePrice * appointment.merchant.late_cancel_fee_value / 10000).toFixed(2)})`
											: `${cs}${(appointment.merchant.late_cancel_fee_value / 100).toFixed(2)}`
										}.
										</Typography>
									</Box>
								) : (
									<Box sx={{ mb: 1 }}>
										<Typography variant="body2" color="text.primary">
											<strong>Late Cancellation:</strong> Free cancellation at any time before the appointment.
										</Typography>
									</Box>
								)}

								{/* No-Show Policy */}
								{(() => {
									// Check for explicit no-show fee
									if (appointment.merchant.no_show_fee_type && appointment.merchant.no_show_fee_value != null) {
										return (
											<Typography variant="body2" color="text.primary">
												<strong>No-Show:</strong> If you don't show up for your appointment, a fee of{' '}
												{appointment.merchant.no_show_fee_type === 'percentage'
											? `${appointment.merchant.no_show_fee_value}% of the service price (${cs}${(totalServicePrice * appointment.merchant.no_show_fee_value / 10000).toFixed(2)})`
											: `${cs}${(appointment.merchant.no_show_fee_value / 100).toFixed(2)}`
												} will be charged.
											</Typography>
										)
									}
									// Fall back to late cancel policy
									else if (appointment.merchant.late_cancel_fee_type && appointment.merchant.late_cancel_fee_value != null) {
										return (
											<Typography variant="body2" color="text.primary">
												<strong>No-Show:</strong> If you don't show up for your appointment, a fee of{' '}
											{appointment.merchant.late_cancel_fee_type === 'percentage'
												? `${appointment.merchant.late_cancel_fee_value}% of the service price (${cs}${(totalServicePrice * appointment.merchant.late_cancel_fee_value / 10000).toFixed(2)})`
												: `${cs}${(appointment.merchant.late_cancel_fee_value / 100).toFixed(2)}`
												} will be charged (same as late cancellation fee).
											</Typography>
										)
									}
									// No fee policy
									else {
										return (
											<Typography variant="body2" color="text.primary">
												<strong>No-Show:</strong> No fee will be charged if you don't show up.
											</Typography>
										)
									}
								})()}
							</Box>
						</Stack>
					</CardContent>
				</Card>

				{/* Actions */}
				<Card>
					<CardContent>
						<Stack direction={isSmall ? 'column' : 'row'} spacing={2}>
							{appointment.user_id && session?.user?.id && appointment.user_id === session.user.id && (
								<Button
									variant="contained"
									onClick={() => navigate('/dashboard')}
									fullWidth={isSmall}
								>
									View All Appointments
								</Button>
							)}

							{appointment.merchant.slug && (
								<Button
									variant={appointment.user_id ? "outlined" : "contained"}
									onClick={() => navigate(`/store/${appointment.merchant.slug}`)}
									fullWidth={isSmall}
								>
									Visit Store Page
								</Button>
							)}

							{appointment.merchant.slug && isUpcoming && (
								<Button
									variant="outlined"
									onClick={() => navigate(`/store/${appointment.merchant.slug}/book`)}
									fullWidth={isSmall}
								>
									Book Another Appointment
								</Button>
							)}
						</Stack>

						{/* Cancel Appointment */}
						{isUpcoming && (appointment.status === 'booked' || appointment.status === 'bookPending') && (
							<Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
								<Tooltip title={canCancelAppointment() ? '' : getCancelDisabledReason()}>
									<span>
										<Button
											variant="outlined"
											color="error"
											startIcon={<CancelIcon />}
											onClick={handleCancelAppointment}
											disabled={!canCancelAppointment() || cancelling}
											fullWidth={isSmall}
											sx={{
												opacity: canCancelAppointment() ? 1 : 0.6,
												cursor: canCancelAppointment() ? 'pointer' : 'not-allowed'
											}}
										>
											{cancelling ? 'Cancelling...' : 'Cancel Appointment'}
										</Button>
									</span>
								</Tooltip>
								{canCancelAppointment() && (() => {
									// For bookPending, always show free cancellation
									if (appointment.status === 'bookPending') {
										return (
											<Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
												✓ Free cancellation (pending appointments can be cancelled anytime without fees)
											</Typography>
										)
									}

									// For booked appointments, check late cancel window
									const now = dayjs()
									const appointmentStart = dayjs(appointment.start_ts).tz(appointment.merchant.timezone)
									const lateCancelHours = appointment.merchant.late_cancel_hours || 24
									const hoursUntilAppointment = appointmentStart.diff(now, 'hours', true)
									const withinLateCancelWindow = hoursUntilAppointment < lateCancelHours

									if (withinLateCancelWindow) {
										return (
											<Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
												⚠️ Cancelling within {lateCancelHours} hours may incur a late cancellation fee.
											</Typography>
										)
									} else {
										return (
											<Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
												✓ Free cancellation (more than {lateCancelHours} hours in advance)
											</Typography>
										)
									}
								})()}
								{!canCancelAppointment() && appointment.merchant.phone && (
									<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
										The appointment time has passed. Please contact the store at {appointment.merchant.phone} if needed.
									</Typography>
								)}
							</Box>
						)}
					</CardContent>
				</Card>

				{errorMessage && (
					<Alert severity="error">{errorMessage}</Alert>
				)}
			</Stack>
		</Container>
	)
} 